import {
  Badge,
  ProgressBar,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import {
  Building16Regular,
  CalendarClock24Regular,
  Checkmark16Regular,
  DataUsage24Regular,
  Fire16Regular,
  Money24Regular,
  Wallet24Regular,
} from '@fluentui/react-icons';
import { readColumnRawValue, toZonedWallClock, type LookupColumnRawValue } from '@powerportalspro/core';
import { RecordContext, useLocale, useRecordContext, useT, useUserTimeZone } from '@powerportalspro/react';
import {
  AggregateDataverseChartDataSource,
  QueryAggregate,
  DateGrouping,
  ChartType,
} from '@powerportalspro/react-charts';
import {
  ColumnEdit,
  DataverseChart,
  DeleteContextButton,
  GridColumnMenuItem,
  GridLayout,
  GridToolbarItems,
  LoseOpportunityButton,
  MainGrid,
  MenuBar,
  NavigateOpenRecordGridButton,
  RefreshContextButton,
  SaveContextButton,
  Section,
  SectionColumn,
  SummaryCard,
  SummaryCards,
  TabPanel,
  Tabs,
  Timeline,
  WinOpportunityButton,
} from '@powerportalspro/react-fluent';
import { useMemo } from 'react';
import { DetailContactCard, heroBand, recordContextTarget, type DetailPageProps } from './DetailPageParts';
import { ACCOUNT_CONTACTS_VIEW_ID, getAccountContactsView } from './DetailPageRecords';

// The opportunity page: the one the grids open, and the opportunity demo. One deal, laid out for
// working it: a header with its name, its account and how it's rated, and a strip of the sales
// stages with the deal's own highlighted, read from its stepname ("1-Qualify" and so on); four
// figures — its estimated revenue, its probability as a bar, its budget, and how far off its close
// date is; Win and Lose, which close the deal while it is open, beside Save; and tabs for the deal's
// fields, the people at its account as cards the page draws (DetailContactCard), its notes and
// activities in a Timeline, and the account's history, charted.
//
// Everything but the stage strip and the days to close is the record the RecordContext loaded,
// formatted by the server. The account's id comes from the deal's customer lookup, so the
// stakeholders and the charts follow whichever account the deal belongs to. A new deal (no id in
// the address) shows only its fields until it is saved.

const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Demos.DetailPages.OpportunityDetail';

// The sales stages, as the deals' stepname counts them: "1-Qualify" is the first.
const STAGES = ['stage-qualify', 'stage-develop', 'stage-propose', 'stage-close'] as const;

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalL,
  },
  hero: {
    ...heroBand,
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXL,
    padding: `${tokens.spacingVerticalXXL} ${tokens.spacingHorizontalXXL}`,
  },
  heroText: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalS,
  },
  eyebrow: {
    fontSize: tokens.fontSizeBase200,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    opacity: 0.85,
  },
  name: {
    margin: 0,
    fontSize: tokens.fontSizeHero800,
    lineHeight: tokens.lineHeightHero800,
    fontWeight: tokens.fontWeightSemibold,
  },
  badges: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
  },
  // The sales stages, joined by a line: those passed are ticked, the current one is filled.
  stages: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    margin: 0,
    padding: 0,
    listStyleType: 'none',
  },
  stage: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    rowGap: tokens.spacingVerticalXS,
    textAlign: 'center',
    fontSize: tokens.fontSizeBase300,
  },
  // The line to the next stage, from the middle of this dot to the middle of that one.
  joined: {
    '::after': {
      content: '""',
      position: 'absolute',
      top: '15px',
      left: 'calc(50% + 20px)',
      right: 'calc(-50% + 20px)',
      height: '2px',
      backgroundColor: tokens.colorNeutralForegroundOnBrand,
      opacity: 0.35,
    },
  },
  joinedDone: {
    '::after': {
      opacity: 1,
    },
  },
  dot: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    boxSizing: 'border-box',
    borderRadius: tokens.borderRadiusCircular,
    border: `${tokens.strokeWidthThick} solid ${tokens.colorNeutralForegroundOnBrand}`,
    fontWeight: tokens.fontWeightSemibold,
  },
  dotFilled: {
    backgroundColor: tokens.colorNeutralForegroundOnBrand,
    color: tokens.colorBrandForeground1,
  },
  dotCurrent: {
    boxShadow: `0 0 0 ${tokens.spacingHorizontalXS} rgb(from ${tokens.colorNeutralForegroundOnBrand} r g b / 0.3)`,
  },
  next: {
    opacity: 0.65,
  },
  current: {
    fontWeight: tokens.fontWeightSemibold,
  },
  charts: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: tokens.spacingHorizontalXL,
  },
});

/** The opportunity page: a fixed deal with `id`, or the one in the address with `queryParameterName`. */
export function OpportunityDetail(props: DetailPageProps) {
  return (
    <RecordContext table="opportunity" {...recordContextTarget(props)} forceSuccessfulValidationBeforeSave>
      <OpportunityPage />
    </RecordContext>
  );
}

function OpportunityPage() {
  const styles = useStyles();
  const t = useT();
  const { record } = useRecordContext();
  // A saved deal: one with a history and people of its own to show. A new one has no id until it is
  // saved.
  const opportunityId = record?.id;

  return (
    <div className={styles.root}>
      {opportunityId && (
        <>
          <OpportunityHero />
          <OpportunityFigures />
        </>
      )}

      <MenuBar>
        <SaveContextButton />
        <RefreshContextButton />
        <DeleteContextButton />
        <WinOpportunityButton />
        <LoseOpportunityButton />
      </MenuBar>

      {/* The selected tab is kept in ?tab=. The summary's fields mount with the page, so the form
          validates and saves them unopened; the other tabs each load when first opened. */}
      <Tabs selectedTabQueryParameter="tab">
        <TabPanel value="summary" header={t(`${STRINGS_BASE}.tab-summary`)} lazyMount={false}>
          <Section columnCount={3}>
            <SectionColumn headerText={t(`${STRINGS_BASE}.section-deal`)}>
              <ColumnEdit columnName="name" />
              <ColumnEdit columnName="customerid" />
              <ColumnEdit columnName="parentcontactid" />
              <ColumnEdit columnName="purchasetimeframe" />
              <ColumnEdit columnName="statecode" />
              <ColumnEdit columnName="statuscode" />
            </SectionColumn>
            <SectionColumn headerText={t(`${STRINGS_BASE}.section-forecast`)}>
              <ColumnEdit columnName="estimatedvalue" />
              <ColumnEdit columnName="budgetamount" />
              <ColumnEdit columnName="closeprobability" />
              <ColumnEdit columnName="estimatedclosedate" />
              <ColumnEdit columnName="opportunityratingcode" />
            </SectionColumn>
            <SectionColumn headerText={t(`${STRINGS_BASE}.section-notes`)}>
              <ColumnEdit columnName="description" />
              <ColumnEdit columnName="customerneed" />
              <ColumnEdit columnName="proposedsolution" />
            </SectionColumn>
          </Section>
        </TabPanel>
        {opportunityId && (
          <>
            <TabPanel value="stakeholders" header={t(`${STRINGS_BASE}.tab-stakeholders`)}>
              <Stakeholders />
            </TabPanel>
            <TabPanel value="timeline" header={t(`${STRINGS_BASE}.tab-timeline`)}>
              <Timeline key={opportunityId} allowFiltering allowSavedViews savedViewsKey="opportunity-detail-timeline" />
            </TabPanel>
            <TabPanel value="history" header={t(`${STRINGS_BASE}.tab-history`)}>
              <AccountHistory />
            </TabPanel>
          </>
        )}
      </Tabs>
    </div>
  );
}

// The deal's account, when its customer is one.
function useAccountId(): string | null {
  const { record } = useRecordContext();
  const customer = readColumnRawValue<LookupColumnRawValue>(record, 'customerid');
  return customer?.tableName === 'account' ? customer.id : null;
}

function OpportunityHero() {
  const styles = useStyles();
  const t = useT();
  const { record } = useRecordContext();
  const formatted = record?.formattedValues ?? {};

  // "2-Develop" is the second stage; a deal with none is at the first.
  const step = readColumnRawValue<string>(record, 'stepname') ?? '';
  const stage = /^\d/.test(step) ? Math.min(Math.max(Number(step[0]) - 1, 0), STAGES.length - 1) : 0;

  return (
    <header className={styles.hero}>
      <div className={styles.heroText}>
        <span className={styles.eyebrow}>{t(`${STRINGS_BASE}.eyebrow`)}</span>
        <h2 className={styles.name}>{readColumnRawValue<string>(record, 'name')}</h2>
        <div className={styles.badges}>
          {formatted['customerid'] && (
            <Badge appearance="tint" color="brand" size="large" icon={<Building16Regular />}>
              {formatted['customerid']}
            </Badge>
          )}
          {formatted['opportunityratingcode'] && (
            <Badge appearance="filled" color="danger" size="large" icon={<Fire16Regular />}>
              {formatted['opportunityratingcode']}
            </Badge>
          )}
          {formatted['msdyn_forecastcategory'] && (
            <Badge appearance="tint" color="informative" size="large">
              {formatted['msdyn_forecastcategory']}
            </Badge>
          )}
        </div>
      </div>
      <ol className={styles.stages}>
        {STAGES.map((key, i) => (
          <li
            key={key}
            className={mergeClasses(
              styles.stage,
              i < STAGES.length - 1 && styles.joined,
              i < STAGES.length - 1 && i < stage && styles.joinedDone,
              i > stage && styles.next,
              i === stage && styles.current,
            )}
            aria-current={i === stage ? 'step' : undefined}
          >
            <span
              className={mergeClasses(styles.dot, i <= stage && styles.dotFilled, i === stage && styles.dotCurrent)}
            >
              {i < stage ? <Checkmark16Regular /> : i + 1}
            </span>
            <span>{t(`${STRINGS_BASE}.${key}`)}</span>
          </li>
        ))}
      </ol>
    </header>
  );
}

function OpportunityFigures() {
  const t = useT();
  const { locale } = useLocale();
  const { record } = useRecordContext();
  const timeZone = useUserTimeZone();
  const probability = readColumnRawValue<number>(record, 'closeprobability');
  const close = readColumnRawValue<Date>(record, 'estimatedclosedate');

  let closeNote: string | null = null;
  if (close) {
    // Today in the user's time zone, which the dates the page shows are in — the browser's until the
    // server has named it.
    const today = timeZone ? toZonedWallClock(new Date(), timeZone) : new Date();
    const days = Math.round(
      (Date.UTC(close.getUTCFullYear(), close.getUTCMonth(), close.getUTCDate()) -
        Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())) /
        86_400_000,
    );
    closeNote =
      days > 0
        ? t(`${STRINGS_BASE}.close-in`, [String(days)])
        : days === 0
          ? t(`${STRINGS_BASE}.close-today`)
          : t(`${STRINGS_BASE}.close-overdue`, [String(-days)]);
  }

  // The deal's own columns, each a SummaryCard; the probability is given its text and a bar.
  return (
    <SummaryCards>
      <SummaryCard icon={<Money24Regular />} label={t(`${STRINGS_BASE}.kpi-revenue`)} columnName="estimatedvalue" />
      <SummaryCard
        icon={<DataUsage24Regular />}
        label={t(`${STRINGS_BASE}.kpi-probability`)}
        value={
          probability == null
            ? null
            : new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }).format(probability / 100)
        }
      >
        <ProgressBar value={(probability ?? 0) / 100} thickness="large" />
      </SummaryCard>
      <SummaryCard icon={<Wallet24Regular />} label={t(`${STRINGS_BASE}.kpi-budget`)} columnName="budgetamount" />
      <SummaryCard
        icon={<CalendarClock24Regular />}
        label={t(`${STRINGS_BASE}.kpi-close-date`)}
        columnName="estimatedclosedate"
        {...(closeNote && { caption: closeNote })}
      />
    </SummaryCards>
  );
}

// Everything the column menu offers but totals, which contacts have nothing worth.
const NO_TOTALS = Object.values(GridColumnMenuItem).filter((item) => item !== GridColumnMenuItem.Totals);

// The people at the deal's account, as cards only, each drawn by the page.
function Stakeholders() {
  const t = useT();
  const accountId = useAccountId();
  const views = useMemo(
    () => (accountId ? [getAccountContactsView(accountId, t(`${STRINGS_BASE}.view-stakeholders`))] : null),
    [accountId, t],
  );

  if (!views) return null;

  return (
    <MainGrid
      key={accountId}
      tableName="contact"
      defaultLayout={GridLayout.Cards}
      cardImageColumn="entityimage"
      cardRenderer={(card) => <DetailContactCard card={card} />}
      minCardWidth="280px"
      maxHeight="520px"
      columnMenuItems={NO_TOTALS}
      customViewDefinitions={views}
      viewIds={[ACCOUNT_CONTACTS_VIEW_ID]}
      defaultViewId={ACCOUNT_CONTACTS_VIEW_ID}
    >
      <GridToolbarItems>
        <NavigateOpenRecordGridButton />
      </GridToolbarItems>
    </MainGrid>
  );
}

// What the account's closed deals were worth, by the year they closed, won and lost stacked — their
// estimated value, not their actual: a lost deal closes with no actual value, so a sum of actual
// values would leave the lost ones out — and all its deals by status.
function AccountHistory() {
  const styles = useStyles();
  const t = useT();
  const accountId = useAccountId();

  const sources = useMemo(
    () =>
      accountId
        ? {
            closedByYear: new AggregateDataverseChartDataSource({
              tableName: 'opportunity',
              groupByColumn: 'actualclosedate',
              groupByDateGrouping: DateGrouping.Year,
              seriesColumn: 'statecode',
              aggregateColumn: 'estimatedvalue',
              aggregate: QueryAggregate.Sum,
              filterXml: `<filter>
                <condition attribute='customerid' operator='eq' value='${accountId}' />
                <condition attribute='statecode' operator='in'><value>1</value><value>2</value></condition>
              </filter>`,
            }),
            byStatus: new AggregateDataverseChartDataSource({
              tableName: 'opportunity',
              groupByColumn: 'statecode',
              aggregateColumn: 'opportunityid',
              aggregate: QueryAggregate.Count,
              filterXml: `<filter><condition attribute='customerid' operator='eq' value='${accountId}' /></filter>`,
            }),
          }
        : null,
    [accountId],
  );

  if (!sources) return null;

  return (
    <div className={styles.charts}>
      <DataverseChart
        dataSource={sources.closedByYear}
        type={ChartType.Bar}
        title={t(`${STRINGS_BASE}.chart-closed-by-year`)}
        height="320px"
        stacked
        yAxisPrefix="$"
      />
      <DataverseChart
        dataSource={sources.byStatus}
        type={ChartType.Doughnut}
        title={t(`${STRINGS_BASE}.chart-by-status`)}
        height="320px"
      />
    </div>
  );
}
